import { DEFAULT_COOKIE_CONSENT, LOCAL_GDPR_CONSENT } from "@/lib/constant";
import type { CookieConsentData } from "@/types";

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

type ConsentState = "granted" | "denied";

let consentDefaultsApplied = false;

/** Ensure dataLayer + gtag stub exist so consent can be set before the GA script loads. */
export function ensureGtagConsentApi() {
	if (typeof window === "undefined") return;

	window.dataLayer = window.dataLayer || [];
	if (typeof window.gtag !== "function") {
		// Must push `arguments`, not a plain array. gtag.js drops arrays, so consent
		// and events queued before the script loads never reach Google.
		window.gtag = function gtag() {
			// eslint-disable-next-line prefer-rest-params
			window.dataLayer!.push(arguments);
		};
	}
}

/**
 * Consent Mode v2 defaults (denied until the user / campaign page grants).
 * Safe to call once per page lifetime before loading GA.
 */
export function applyGoogleConsentDefaults() {
	if (typeof window === "undefined" || consentDefaultsApplied) return;

	ensureGtagConsentApi();
	window.gtag?.("consent", "default", {
		ad_storage: "denied",
		ad_user_data: "denied",
		ad_personalization: "denied",
		analytics_storage: "denied",
		wait_for_update: 500,
	});
	consentDefaultsApplied = true;
}

/**
 * Map Techxeed cookie toggles → Google Consent Mode v2.
 * - performance → analytics_storage
 * - advertisements → ad_storage, ad_user_data, ad_personalization
 */
export function applyGoogleConsentFromCookieConsent(
	consent: Pick<CookieConsentData, "performance" | "advertisements">,
) {
	if (typeof window === "undefined") return;

	ensureGtagConsentApi();
	applyGoogleConsentDefaults();

	const analytics: ConsentState = consent.performance === true ? "granted" : "denied";
	const ads: ConsentState = consent.advertisements === true ? "granted" : "denied";

	window.gtag?.("consent", "update", {
		analytics_storage: analytics,
		ad_storage: ads,
		ad_user_data: ads,
		ad_personalization: ads,
	});
}

/** Read stored cookie toggles (null if missing / invalid). */
export function readStoredCookieConsent(): Partial<CookieConsentData> | null {
	if (typeof window === "undefined") return null;
	try {
		const raw = localStorage.getItem(LOCAL_GDPR_CONSENT);
		if (!raw || !raw.startsWith("{")) return null;
		return JSON.parse(raw) as Partial<CookieConsentData>;
	} catch {
		return null;
	}
}

/** Re-apply stored cookie choices so a custom event is not sent while consent is still denied. */
export function syncGoogleConsentFromStorage() {
	const parsed = readStoredCookieConsent();
	if (!parsed) return;
	applyGoogleConsentFromCookieConsent({
		performance: parsed.performance === true,
		advertisements: parsed.advertisements === true,
	});
}

/** Ads conversion tags need ad_storage granted (advertisements cookie). */
export function hasConsentForAds(): boolean {
	const parsed = readStoredCookieConsent();
	return parsed?.advertisements === true;
}

const AUTO_TRACKING_PREFIXES = ["/order-status", "/basket", "/services", "/categories"];

/**
 * Checkout and service pages grant all cookies without waiting for the cookie banner.
 * The banner chunk loads late, so the purchase must write consent itself or Ads never fires.
 */
export function ensureAutoTrackingConsent() {
	if (typeof window === "undefined") return;
	const path = window.location.pathname;
	if (!AUTO_TRACKING_PREFIXES.some((prefix) => path.startsWith(prefix))) return;

	const parsed = readStoredCookieConsent();
	const alreadyGranted =
		parsed?.required === true && parsed.performance === true && parsed.advertisements === true;
	if (!alreadyGranted) {
		localStorage.setItem(LOCAL_GDPR_CONSENT, JSON.stringify(DEFAULT_COOKIE_CONSENT));
	}
	syncGoogleConsentFromStorage();
}
